import { BUTTON_SIZE, BUTTON_TYPE, DSButton } from '@repo/design-system/index'
import { QIcon_bell_off, QIcon_bookmark, QIcon_effect } from '@repo/icons/index'
import classNames from 'classnames'
import React, { useState } from 'react'
import { useEventTracking } from '../../helpers/gtmHelper'
import { ITutor } from '../../model/tutor.interface'
import { IAvailableTime } from '../../modules/apis/student/lesson/schedule/types'
import useIsMobile from '../../modules/hooks/common/useIsMobile'
import { n_common, n_home, n_tutor_lists, n_tutors, s_tutors } from '../../modules/i18n/strings/auto/wording'
import useTranslation from '../../modules/i18n/useTranslation'
import { isB2BHRGroup } from '../../modules/utils'
import { COLORS, TUTOR_SCHEDULE_OPEN_STATUS } from '../../modules/vars'
import Skeleton from '../skeleton'

export interface TutorCardProps {
  tutor: ITutor
  className?: string
  id?: number
  noRate?: boolean
  currentUser?: any
  vertical?: boolean
  isRecommend?: boolean
  availableTimes?: IAvailableTime[]
  showAvailableCount?: boolean
  timeslotId?: number
  selected?: boolean
  buttonType?: BUTTON_TYPE
  showKeyword?: boolean
  showRecommendReason?: boolean
  onClick?: (e: React.MouseEvent) => void
  onClickHeart?: (e: React.MouseEvent) => void
  handleAvatarClick?: (e: React.MouseEvent, tutor: ITutor) => void
  handleAvailableButtonClick?: (e: React.MouseEvent, tutor: ITutor) => void
  handleScheduleClick?: (e: React.MouseEvent, time: IAvailableTime, tutor: ITutor) => void
  index?: number
  handshakeExpiredAt?: string
  handshakeLabel?: string
}

interface TutorCardSkeletonProps {
  className?: string
}

const TutorCardVerticalSkeleton = ({ className }: TutorCardSkeletonProps) => {
  const isMobile = useIsMobile()

  return (
    <div
      className={classNames(
        className,
        'border-b-1 border-1 relative flex cursor-pointer flex-col rounded-[8px] p-[16px] py-[20px] sm:py-[16px]'
      )}
    >
      <Skeleton.Rectangular className="rounded-[8px]" w={isMobile ? 72 : 92} h={isMobile ? 72 : 115} />
      <div className="flex flex-col gap-[16px] mt-[16px] md:mt-[12px]">
        <div className="flex flex-col items-start justify-start gap-[8px]">
          <div className="w-[60px] h-[18px]" />
          <Skeleton.Rectangular h={isMobile ? 18 : 19} w={80} />
          <Skeleton.Rectangular h={isMobile ? 11 : 14} w={140} />
          <Skeleton.Rectangular h={isMobile ? 11 : 14} w={76} />
        </div>
        <Skeleton.Rectangular h={isMobile ? 15 : 18} w={109} />
      </div>
      <div className="absolute top-[16px] right-[16px] z-10 h-[20px] w-[16px] animate-pulse cursor-pointer rounded-md bg-gray-100"></div>
    </div>
  )
}

const TutorCardHorizontalSkeleton = ({ className }: TutorCardSkeletonProps) => {
  return (
    <div
      className={classNames(
        className,
        'border-b-1 flex flex-row items-start justify-between rounded-[8px] border-gray-100 py-[20px] lg:h-full lg:max-h-[269px] lg:flex-col'
      )}
    >
      <Skeleton.Circle w={72} h={72} />
      <div className="ml-[16px] flex flex-col items-start justify-start pr-[70px]">
        <Skeleton.Rectangular w={80} h={18} />
        <Skeleton.Rectangular w={163} h={24} className="mt-[4px]" />
        <Skeleton.Rectangular w={80} h={18} className="mt-[4px]" />
        <Skeleton.Rectangular w={80} h={18} className="mt-[4px]" />
        <Skeleton.Rectangular w={109} h={18} className="mt-[8px] mr-[12px]" />
      </div>
      <div className="mt-[8px]">
        <Skeleton.Rectangular w={16} h={20} />
      </div>
    </div>
  )
}

const TutorCard = ({
  tutor,
  className,
  currentUser,
  vertical,
  isRecommend,
  availableTimes = [],
  showAvailableCount,
  timeslotId,
  selected,
  buttonType,
  onClickHeart,
  handleAvatarClick,
  handleAvailableButtonClick,
  handleScheduleClick,
  showKeyword,
  showRecommendReason,
  index,
  handshakeExpiredAt,
  handshakeLabel,
}: TutorCardProps) => {
  const {
    is_mine,
    id,
    is_my_tutor,
    name,
    first_name,
    school,
    major,
    image_url,
    schedule_status,
    teach_junior,
    primary_keyword,
    is_rookie,
    available_count,
  } = tutor
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const [isHover, setIsHover] = useState(false)
  const isSMSize = useIsMobile(undefined, 1040)
  const { sendClickEvent } = useEventTracking()
  const isB2BUser = isB2BHRGroup(currentUser)

  const isFavorite = is_mine || is_my_tutor
  const isNotificationEnabled = tutor.tutor_notification

  const getCustomBgColor = (time: IAvailableTime) => {
    if (timeslotId == time.timeslotId) {
      return COLORS.PURPLE50
    } else if (time?.classtime?.is_today) {
      return COLORS.RED50
    } else {
      return COLORS.WHITE
    }
  }

  const getOpacity = (time: IAvailableTime) => {
    const { classtime } = time
    if (!classtime?.is_available && classtime?.is_opened) {
      return 'opacity-30'
    } else {
      return ''
    }
  }

  const getTutorType = () => {
    if (is_rookie) {
      return 'rookie'
    } else if (isRecommend) {
      return 'recommend'
    } else if (isFavorite) {
      return 'favorite'
    } else {
      return null
    }
  }

  const getTimeObjectContext = (index) => {
    if (index == 0) {
      return 'before_time'
    } else if (index == 1) {
      return 'on_time'
    } else {
      return 'next_time'
    }
  }

  if (!id && vertical) {
    return <TutorCardVerticalSkeleton className={className} />
  }
  if (!id && !vertical && isMobile) {
    return <TutorCardHorizontalSkeleton className={className} />
  }

  return (
    <div
      className={classNames(className, 'border-1 relative flex flex-col cursor-pointer gap-[0px] p-[16px]', {
        'hover:bg-gray-50': !isHover,
        'h-full rounded-[8px] px-[16px] py-[20px] sm:py-[16px]': vertical,
        'lg:border-purple-500 lg:bg-gray-50': selected,
        'border-gray-100 lg:border-gray-200 ': !selected,
        'lg:h-full rounded-[8px]': !vertical,
      })}
    >
      <div
        className={classNames('flex', {
          'flex-col': vertical,
        })}
      >
        <div
          className="relative h-[72px] min-w-[72px] sm:h-[115px] sm:min-w-[96px]"
          onClick={(e) => {
            availableTimes?.length > 0 &&
              sendClickEvent({ button_name: 'tutor_card', location_detail: `${index}`, object_title: getTutorType() })
            handleAvatarClick(e, tutor)
          }}
        >
          <img
            height={isMobile ? 72 : 115}
            width={isMobile ? 72 : 96}
            style={{ width: isMobile ? '72px' : '96px', height: isMobile ? '72px' : '115px' }}
            className={classNames('rounded-[8px] object-cover', {
              'opacity-50': schedule_status && schedule_status != TUTOR_SCHEDULE_OPEN_STATUS.AVAILABLE,
            })}
            src={image_url || ''}
            alt={name}
          />
        </div>
        <div
          onClick={(e) => {
            availableTimes?.length > 0 &&
              sendClickEvent({ button_name: 'tutor_card', location_detail: `${index}`, object_title: getTutorType() })
            handleAvatarClick(e, tutor)
          }}
          className={classNames('relative w-full text-left', {
            'mt-[16px] sm:mt-[12px]': vertical,
            'ml-[20px]': !vertical,
          })}
        >
          <div
            className={classNames({
              'h-[89px] md:max-h-max': vertical,
            })}
          >
            {/* 추가 설명 (ex: 50% 포인트백) */}
            <div
              className={classNames('flex flex-row gap-x-[8px] text-left whitespace-nowrap', {
                'h-[22px]': vertical,
              })}
            >
              {handshakeLabel && <div className="Caption-12Md sm:Body2-14Bd text-purple-600">{handshakeLabel}</div>}
              {!handshakeLabel && schedule_status == TUTOR_SCHEDULE_OPEN_STATUS.FULL_BOOKED && (
                <div className="Caption-12Md sm:Body2-14Bd text-gray-500">{t(n_tutors.schedule_fully_booked)}</div>
              )}
              {!handshakeLabel && schedule_status == TUTOR_SCHEDULE_OPEN_STATUS.NOT_OPEN_YET && (
                <div className="Caption-12Md sm:Body2-14Bd text-gray-500">{t(n_tutors.btn_schedule)}</div>
              )}
              {!handshakeLabel && isRecommend && (
                <div className="Caption-12Md sm:Body2-14Bd text-purple-600">{t(n_common.label_featured)}</div>
              )}
              {is_rookie && !isB2BUser && (
                <div className="Caption-12Md sm:Body2-14Bd text-red-400">{t(n_common.label_pointback)}</div>
              )}
              {currentUser && currentUser.is_junior && !teach_junior && (
                <div className="Caption-12Md sm:Body2-14Bd text-blue-500">{t(s_tutors.adults_only)}</div>
              )}
            </div>

            {/* 튜터 이름, 전공, 대학, 해시 태그, 예약 가능 시간 */}

            <div>
              <div className="flex items-center max-w-[250px] overflow-hidden gap-x-4">
                <span className="truncate Body1-16Bd sm:Title2-18Bd min-w-0 max-w-full">
                  {name ? name : first_name}
                </span>
                {isFavorite && !isNotificationEnabled && (
                  <QIcon_bell_off width={20} height={20} color={COLORS.GRAY300} className="flex-shrink-0 ml-2" />
                )}
              </div>
              <div
                className={classNames(
                  'Caption-12Md sm:Body2-14Md line-clamp-1 max-w-[250px] overflow-hidden text-black',
                  {}
                )}
              >
                {major}
              </div>
              <h5
                className={classNames(
                  'Caption-12Md sm:Body2-14Md line-clamp-1 max-w-[250px] overflow-hidden text-gray-500'
                )}
              >
                {school}
              </h5>
            </div>
          </div>

          {/* 태그 */}
          <div
            className={classNames(
              'Caption-12Bd sm:Body2-14Bd mt-[8px] sm:mt-[12px] text-gray-500 h-[19px] sm:h-[22px]'
            )}
          >
            {primary_keyword && `#${primary_keyword}`}
          </div>
        </div>
        {/* 예약 가능 여부 */}
        {showAvailableCount && (
          <div className="Body2-14Bd mt-[16px] text-right text-blue-500">
            {t(n_home.favorites_tutor.slot)}: {available_count}
          </div>
        )}
        <div
          className={classNames(
            'mt-[8px] ml-auto flex w-full max-w-[80px] whitespace-nowrap md:hidden md:max-w-[100px] items-end',
            {
              hidden: vertical,
            }
          )}
        >
          {schedule_status == TUTOR_SCHEDULE_OPEN_STATUS.AVAILABLE && (
            <div onMouseEnter={() => setIsHover(true)} onMouseLeave={() => setIsHover(false)}>
              <DSButton
                className="hover:bg-gray-50"
                type={buttonType ? buttonType : BUTTON_TYPE.OUTLINE}
                size={isSMSize ? BUTTON_SIZE.SM : BUTTON_SIZE.MD}
                onClick={(e) => (handleAvailableButtonClick ? handleAvailableButtonClick(e, tutor) : null)}
              >
                <DSButton.Label>{t(n_common.btn_book)}</DSButton.Label>
              </DSButton>
            </div>
          )}
          {schedule_status == TUTOR_SCHEDULE_OPEN_STATUS.FULL_BOOKED && (
            <DSButton
              disabled={true}
              type={BUTTON_TYPE.OUTLINE}
              size={isSMSize ? BUTTON_SIZE.SM : BUTTON_SIZE.MD}
              onClick={null}
            >
              <DSButton.Label>{t(n_common.btn_book)}</DSButton.Label>
            </DSButton>
          )}
          {schedule_status == TUTOR_SCHEDULE_OPEN_STATUS.NOT_OPEN_YET && (
            <DSButton
              disabled
              type={BUTTON_TYPE.OUTLINE}
              size={isSMSize ? BUTTON_SIZE.SM : BUTTON_SIZE.MD}
              onClick={null}
            >
              <DSButton.Label>{t(n_common.btn_book)}</DSButton.Label>
            </DSButton>
          )}
        </div>
      </div>
      <div>
        {availableTimes?.length > 0 && (
          <div className="hidden items-center gap-x-[8px] sm:grid grid-cols-3 mt-[16px]">
            {availableTimes.map((time, i) => {
              return (
                <div key={i}>
                  <DSButton
                    customBgColor={getCustomBgColor(time)}
                    className={getOpacity(time)}
                    customBorderColor={time.value && COLORS.PURPLE600}
                    disabled={!time.value}
                    type={BUTTON_TYPE.OUTLINE}
                    size={BUTTON_SIZE.MD}
                    onClick={(e) => {
                      sendClickEvent({
                        button_name: 'tutor_card_book',
                        location_detail: `${index}`,
                        object_title: getTutorType(),
                        object_context: getTimeObjectContext(i),
                      })
                      handleScheduleClick(e, time, tutor)
                    }}
                  >
                    <DSButton.Label customTxtColor={time.value && COLORS.PURPLE600}>
                      {time.display || '-'}
                    </DSButton.Label>
                  </DSButton>
                </div>
              )
            })}
          </div>
        )}
        {availableTimes?.length > 0 && (
          <div className="mt-[16px] flex items-center gap-x-[8px] sm:hidden">
            {availableTimes.map((time, i) => {
              return (
                <DSButton
                  customBgColor={getCustomBgColor(time)}
                  className={getOpacity(time)}
                  customBorderColor={time.value && COLORS.PURPLE600}
                  disabled={!time.value}
                  key={i}
                  type={BUTTON_TYPE.OUTLINE}
                  size={BUTTON_SIZE.SM}
                  onClick={(e) => {
                    sendClickEvent({
                      button_name: 'tutor_card_book',
                      location_detail: `${index}`,
                      object_title: getTutorType(),
                      object_context: getTimeObjectContext(i),
                    })
                    handleScheduleClick(e, time, tutor)
                  }}
                >
                  <DSButton.Label customTxtColor={time.value && COLORS.PURPLE600}>{time.display || '-'}</DSButton.Label>
                </DSButton>
              )
            })}
          </div>
        )}
      </div>
      {showKeyword && (
        <div className="mt-[16px]">
          <div className="flex gap-x-[4px] items-center">
            <QIcon_effect width={20} height={20} color={COLORS.PURPLE400} />
            <div className="Body2-14Bd">{t(n_tutor_lists.keywords_title)}</div>
          </div>
          <div className="mt-[8px] flex flex-wrap gap-[8px] p-[8px] bg-gray-50 rounded-[8px]">
            {tutor?.evaluation_keywords?.slice(0, 3)?.map((item, index) => {
              return (
                <div className="Caption-12Bd text-gray-900 bg-gray-100 px-[6px] py-[2px] rounded-[4px]" key={index}>
                  {item}
                </div>
              )
            })}
          </div>
        </div>
      )}
      {showRecommendReason && (
        <div className="mt-[16px]">
          <div className="flex gap-x-[4px] items-center">
            <QIcon_effect width={20} height={20} color={COLORS.PURPLE400} />
            <div className="Body2-14Bd">{t(n_tutor_lists.recommend_title)}</div>
          </div>
          <div className="rounded-[4px] bg-gray-50 Body2-14Md text-black mt-[4px] p-[8px]">
            {t(n_tutor_lists.recommend_desc)}
          </div>
        </div>
      )}
      <div
        className={classNames(
          'right-[16px] bottom-[16px] hidden max-w-[80px] whitespace-nowrap md:absolute md:max-w-[100px]',
          {
            hidden: vertical,
            'md:flex': !vertical,
          }
        )}
      >
        {schedule_status == TUTOR_SCHEDULE_OPEN_STATUS.AVAILABLE && (
          <div onMouseEnter={() => setIsHover(true)} onMouseLeave={() => setIsHover(false)}>
            <DSButton
              className="hover:bg-gray-50"
              type={buttonType ? buttonType : BUTTON_TYPE.OUTLINE}
              size={isMobile ? BUTTON_SIZE.SM : BUTTON_SIZE.MD}
              onClick={(e) => (handleAvailableButtonClick ? handleAvailableButtonClick(e, tutor) : null)}
            >
              <DSButton.Label>{t(n_common.btn_book)}</DSButton.Label>
            </DSButton>
          </div>
        )}
        {schedule_status == TUTOR_SCHEDULE_OPEN_STATUS.FULL_BOOKED && (
          <DSButton
            disabled={true}
            type={BUTTON_TYPE.OUTLINE}
            size={isMobile ? BUTTON_SIZE.SM : BUTTON_SIZE.MD}
            onClick={null}
          >
            <DSButton.Label>{t(n_common.btn_book)}</DSButton.Label>
          </DSButton>
        )}
        {schedule_status == TUTOR_SCHEDULE_OPEN_STATUS.NOT_OPEN_YET && (
          <DSButton
            disabled
            type={BUTTON_TYPE.OUTLINE}
            size={isMobile ? BUTTON_SIZE.SM : BUTTON_SIZE.MD}
            onClick={null}
          >
            <DSButton.Label>{t(n_common.btn_book)}</DSButton.Label>
          </DSButton>
        )}
      </div>
      <div
        onClick={(e) => (onClickHeart ? onClickHeart(e) : null)}
        className="absolute top-[16px] right-[16px] z-10 cursor-pointer"
      >
        <QIcon_bookmark width={20} height={20} color={isFavorite ? COLORS.RED400 : COLORS.GRAY300} />
      </div>
    </div>
  )
}

export default TutorCard
